import { useCallback } from 'react';

import RNBootSplash from 'react-native-bootsplash';
import { useDispatch } from 'react-redux';

import store from 'store';

import { LANGUAGE_KEYS } from 'config/languageKeys.ts';

import { SCREEN } from 'router/screens';

import { getBookmarks } from 'store/bookmarks/actions.ts';
import { getChannels } from 'store/programs/actions.ts';
import { getMainMenuConfig } from 'store/railsBuilder/actions.ts';
import {
    changeUserProfile,
    getEntitlements,
    getFavouritesChannelsList,
    getProfilesList,
} from 'store/user/actions';
import {
    CapabilityTypes,
    Entitlement,
    Profile,
} from 'store/user/reducer.types';
import { selectIsNonInteractiveUI } from 'store/user/selectors';
import { isCapabilityValid } from 'store/utils';

import { RegularStorage, StorageKeys } from 'services/mmkv';
import { RetryController } from 'services/opf-retry-mechanism';
import { isErrorNeedsRetry } from 'services/opf-retry-mechanism/utils.ts';

import debugLog from 'utils/debugLog.ts';
import { navigationRef } from 'utils/navigation/navigationUtils';

function useEnterTheApp() {
    const dispatch = useDispatch();

    const tryToEnterTheApp = useCallback(
        async (shouldSkipProfilePage?: boolean) => {
            dispatch(getFavouritesChannelsList());

            // Obligate data which is required for app start(openning Home screen).
            const [entitlements, profiles] = await Promise.all<
                [Entitlement[] | undefined, Profile[]]
                // @ts-ignore
            >([
                new Promise((resolve, reject) => {
                    dispatch(
                        getEntitlements({
                            autoRetry: false,
                            onSuccess: resolve,
                            onFailure: reject,
                        }),
                    );
                }),
                new Promise((resolve, reject) => {
                    dispatch(
                        getProfilesList({
                            onSuccess: resolve,
                            onFailure: reject,
                        }),
                    );
                }),
                new Promise((resolve, reject) => {
                    dispatch(
                        getChannels({
                            autoRetry: false,
                            onSuccess: resolve,
                            onFailure: reject,
                        }),
                    );
                }),
            ]);
            const profilesFTIShown = await RegularStorage.getItem(
                StorageKeys.FTI_PROFILES_SHOWN,
            );

            const validSubscriptions = entitlements?.filter(
                (maybeSubscription) =>
                    maybeSubscription.productType === 'SUBSCRIPTION' &&
                    isCapabilityValid(maybeSubscription),
            );
            const collectionSuspensionActive =
                validSubscriptions?.length === 1 &&
                validSubscriptions[0].productId === CapabilityTypes.MUST_CARRY;
            const isNonInteractive = selectIsNonInteractiveUI(store.getState());

            if (isNonInteractive) {
                await RegularStorage.setItem(
                    StorageKeys.TUTORIAL_PAGE_SHOWN,
                    JSON.stringify(true),
                );
            }

            const defaultProfile = profiles.find(
                ({ default: _default }) => _default,
            );

            if (
                (collectionSuspensionActive ||
                    (profilesFTIShown && profiles.length === 1) ||
                    isNonInteractive) &&
                defaultProfile
            ) {
                const retryChangeProfile = () =>
                    new Promise<void>((resolve, reject) => {
                        dispatch(
                            changeUserProfile(defaultProfile._id, {
                                onSuccess() {
                                    navigationRef.current?.reset({
                                        routes: [
                                            {
                                                name: SCREEN.PERSONALIZE_SCREEN,
                                            },
                                        ],
                                    });
                                    RNBootSplash.hide({ fade: true });
                                    resolve();
                                },
                                onFailure: reject,
                            }),
                        );
                    });

                await new Promise<void>((resolve, reject) => {
                    dispatch(
                        changeUserProfile(defaultProfile._id, {
                            onSuccess() {
                                navigationRef.current?.reset({
                                    routes: [
                                        { name: SCREEN.PERSONALIZE_SCREEN },
                                    ],
                                });
                                resolve();
                            },
                            onFailure: (error) => {
                                if (isErrorNeedsRetry(error)) {
                                    RetryController.showVitalDialog({
                                        actionButtonTitle:
                                            LANGUAGE_KEYS.RELOAD_BUTTON,
                                        actionButtonOnPress: retryChangeProfile,
                                    });
                                    RetryController.registerRetryCallback(
                                        retryChangeProfile,
                                        {
                                            retryDuration: 10 * 60 * 1000,
                                            maxRetryCount: 10,
                                        },
                                    );
                                    RetryController.executeRetry();
                                } else {
                                    reject(error);
                                }
                            },
                        }),
                    );
                });
            } else if (shouldSkipProfilePage) {
                navigationRef.current?.reset({
                    routes: [{ name: SCREEN.HOME }],
                });
            } else {
                navigationRef.current?.reset({
                    routes: [{ name: SCREEN.PROFILES }],
                });
            }

            dispatch(getMainMenuConfig());
            dispatch(getBookmarks());

            await RNBootSplash.hide({ fade: true });
        },
        [],
    );

    const onEnterTheApp = useCallback(
        async (shouldSkipProfilePage: boolean = false) => {
            try {
                await tryToEnterTheApp(shouldSkipProfilePage);
            } catch (error) {
                debugLog('onEnterTheApp', 'error', error);

                if (isErrorNeedsRetry(error)) {
                    RetryController.showVitalDialog({
                        actionButtonTitle: LANGUAGE_KEYS.RELOAD_BUTTON,
                        actionButtonOnPress: tryToEnterTheApp,
                    });
                    RetryController.registerRetryCallback(tryToEnterTheApp, {
                        retryDuration: 5 * 60 * 1000,
                        maxRetryCount: 5,
                    });
                    RetryController.executeRetry();
                } else {
                    // Fallback to Profiles page in case of unknown error.
                    navigationRef.current?.reset({
                        routes: [{ name: SCREEN.PROFILES }],
                    });
                }
            }
        },
        [dispatch],
    );

    return { onEnterTheApp };
}

export default useEnterTheApp;
